// components/create/CoverImageSection.js
//
// The cover, at whatever shape the cover actually is.
//
// ── What was wrong ─────────────────────────────────────────────────────────
//
// The previous version computed its size in JavaScript from a hardcoded
// `maxWidth = 760`, read `window.innerHeight` once on mount, and wrote the
// result to inline `width`/`height` in pixels. On a 390px phone that is a
// 760px-wide box: it overflowed the screen, and because the number never
// recomputed, rotating the phone or resizing the window left it wrong.
//
// Now the element is fluid — `width: 100%` — and the shape comes from the CSS
// `aspect-ratio` property, set from the image's own dimensions. The browser
// does the arithmetic, at every width, for free. A portrait cover is clamped
// so it cannot swallow the screen, which is the one thing the old maths was
// right about.

import React, { useState, useEffect } from 'react';
import '../../styles/CoverImageSection.scss';
import Icon from '../Icon';

/**
 * How far from square a cover is allowed to get before it is cropped.
 *
 * A 9:16 phone photo at full width would be taller than the screen, so the
 * whole form disappears below it. 0.72 (roughly 3:4) keeps a portrait cover
 * clearly portrait while leaving the title and date visible under it. The wide
 * end stops a panorama becoming a letterbox strip.
 */
const MIN_RATIO = 0.72;
const MAX_RATIO = 1.9;

/** What an empty picker shows: the app's card ratio. */
const DEFAULT_RATIO = 1.15;

function displayUri(image) {
  if (!image) return null;
  if (image.isAnimated && image.type === 'gif') {
    return image.gifCdnUrl || image.gifB2Url || image.downsizedUrl || image.uri;
  }
  if (image.type === 'youtube') {
    return image.thumbnailUrl || image.uri;
  }
  if (image.type === 'pdf') {
    // A converted PDF has page images; a just-picked one has only the blob
    // URL of the file itself, which an <img> cannot draw — so that case
    // returns null and the frame shows the labelled card instead of a broken
    // image. The real first page arrives after publishing.
    return image.thumbnailUri || image.pageImages?.[0] || null;
  }
  return image.cdnUrl || image.b2Url || image.uri;
}

export default function CoverImageSection({ coverImage, onPress }) {
  const [ratio, setRatio] = useState(DEFAULT_RATIO);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error

  const uri = displayUri(coverImage);

  useEffect(() => {
    if (!uri) {
      setRatio(DEFAULT_RATIO);
      setStatus('idle');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    const probe = new Image();
    probe.onload = () => {
      if (cancelled) return;
      const natural = probe.width / probe.height;
      setRatio(Math.min(MAX_RATIO, Math.max(MIN_RATIO, natural || DEFAULT_RATIO)));
      setStatus('ready');
    };
    probe.onerror = () => {
      if (cancelled) return;
      setRatio(DEFAULT_RATIO);
      setStatus('error');
    };
    probe.src = uri;

    return () => { cancelled = true; };
  }, [uri]);

  return (
    <div className="cover-section">
      <button
        type="button"
        className="cover-frame"
        style={{ aspectRatio: String(ratio) }}
        onClick={onPress}
        aria-label={coverImage ? 'Change cover' : 'Add a cover'}
      >
        {uri && status !== 'error' ? (
          <>
            <img src={uri} alt="" className="cover-img" />

            {status === 'loading' && (
              <span className="cover-loading" aria-hidden="true">
                <span className="spinner" />
              </span>
            )}

            {coverImage?.isAnimated && (
              <span className="cover-chip">
                <Icon name="play-circle" size={13} />
                GIF
              </span>
            )}
            {coverImage?.type === 'youtube' && (
              <span className="cover-chip">
                <Icon name="play-circle" size={13} />
                Video
              </span>
            )}
            {coverImage?.type === 'pdf' && (
              <span className="cover-chip">
                <Icon name="document-outline" size={13} />
                {coverImage.pageCount ? `PDF · ${coverImage.pageCount} pages` : 'PDF'}
              </span>
            )}
          </>
        ) : coverImage?.type === 'pdf' ? (
          // Chosen but not yet converted. Saying what it is beats an empty
          // frame that looks like nothing was picked.
          <span className="cover-empty cover-empty--pdf">
            <Icon name="document-outline" size={30} />
            <span>{coverImage.fileName || 'PDF invitation'}</span>
            <small>
              {coverImage.pageCount ? `${coverImage.pageCount} pages · ` : ''}
              The pages become your cover when you publish
            </small>
          </span>
        ) : (
          <span className="cover-empty">
            <Icon name={status === 'error' ? 'alert-circle' : 'image-outline'} size={30} />
            <span>
              {status === 'error'
                ? 'That image would not load — pick another'
                : 'Add a cover'}
            </span>
          </span>
        )}

        {/* Always visible, not hover-only: on a touch screen there is no
            hover, and the old overlay button only appeared on one. */}
        <span className="cover-edit" aria-hidden="true">
          <Icon name="camera" size={16} />
          <span>{coverImage ? 'Change' : 'Add'}</span>
        </span>
      </button>
    </div>
  );
}
